import React, { useState } from 'react';
import {
  Box, Button, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Typography,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  removeEntityAdmin, deleteEntityAdminAccount,
  removeGmsAccountant, deleteGmsAccountantAccount,
} from '../../api/memberApi';

// ── Styles ────────────────────────────────────────────────────────────────────

const C = {
  red:    '#dc2626',
  redBg:  '#fef2f2',
  border: '#e2e8f0',
  muted:  '#64748b',
  dark:   '#1e293b',
};

const btnBase = {
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 13,
  px: 2,
  py: 0.875,
};

const redBtn      = { ...btnBase, bgcolor: C.red, color: 'white', '&:hover': { bgcolor: '#b91c1c' }, '&.Mui-disabled': { bgcolor: 'rgba(220,38,38,0.35)', color: 'white' } };
const outlinedBtn = { ...btnBase, borderColor: C.border, color: C.dark, '&:hover': { bgcolor: '#f8fafc' } };

function WarningBox({ children }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', bgcolor: C.redBg, border: '1px solid #fecaca', borderRadius: '10px', p: 1.75, mt: 1.5 }}>
      <WarningAmberRoundedIcon sx={{ color: C.red, fontSize: 20, flexShrink: 0, mt: 0.1 }} />
      <Typography sx={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.6 }}>{children}</Typography>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Props:
 *   open        bool
 *   admin       { userId, name, email } | null
 *   type        'entity-admin' | 'gms-accountant'
 *   entityId    number  — required when type === 'entity-admin'
 *   onClose     () => void
 *   onRemoved   (userId) => void   — role stripped, user kept
 *   onDeleted   (userId) => void   — account permanently deleted
 */
export default function DeleteAdminDialog({ open, admin, type, entityId, onClose, onRemoved, onDeleted }) {
  // 'confirm-remove' | 'confirm-delete' | 'busy'
  const [step, setStep] = useState('confirm-remove');
  const [busy, setBusy] = useState(false);

  const name      = admin?.name || admin?.email || 'This user';
  const roleLabel = type === 'entity-admin' ? 'Entity Admin' : 'GMS Accountant';

  const handleClose = () => {
    if (busy) return;
    setStep('confirm-remove');
    onClose();
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      const result = type === 'entity-admin'
        ? await removeEntityAdmin(entityId, admin.userId)
        : await removeGmsAccountant(admin.userId);

      if (result.hasActiveSpaces) {
        onRemoved(admin.userId);
        setStep('confirm-remove');
        onClose();
      } else {
        setStep('confirm-delete');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDeletePermanently = async () => {
    setBusy(true);
    try {
      if (type === 'entity-admin') {
        await deleteEntityAdminAccount(entityId, admin.userId);
      } else {
        await deleteGmsAccountantAccount(admin.userId);
      }
      onDeleted(admin.userId);
      setStep('confirm-remove');
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const handleKeepAccount = () => {
    onRemoved(admin.userId);
    setStep('confirm-remove');
    onClose();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const renderContent = () => {
    if (step === 'confirm-remove') {
      return (
        <Box>
          <Typography sx={{ fontSize: 13, color: C.dark, lineHeight: 1.7 }}>
            Remove <strong>{name}</strong> as <strong>{roleLabel}</strong>?
            They will lose this role immediately.
          </Typography>
        </Box>
      );
    }

    if (step === 'confirm-delete') {
      return (
        <Box>
          <Typography sx={{ fontSize: 13, color: C.dark, lineHeight: 1.7 }}>
            <strong>{name}</strong> has been removed as {roleLabel} and has no remaining
            space access in the system.
          </Typography>
          <WarningBox>
            Would you like to <strong>permanently delete their account</strong>? This cannot
            be undone. Choose <em>Keep Account</em> to leave the account intact with no active roles.
          </WarningBox>
        </Box>
      );
    }

    return null;
  };

  const renderActions = () => {
    if (step === 'confirm-remove') {
      return (
        <>
          <Button variant="outlined" onClick={handleClose} disabled={busy} sx={outlinedBtn}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRemove}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
            sx={redBtn}
          >
            {busy ? 'Removing…' : 'Remove Role'}
          </Button>
        </>
      );
    }

    if (step === 'confirm-delete') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleDeletePermanently}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
            sx={redBtn}
          >
            {busy ? 'Deleting…' : 'Yes, Delete Permanently'}
          </Button>
          <Button variant="outlined" fullWidth onClick={handleKeepAccount} disabled={busy} sx={outlinedBtn}>
            Keep Account
          </Button>
        </Box>
      );
    }

    return null;
  };

  const titleMap = {
    'confirm-remove': `Remove ${roleLabel}`,
    'confirm-delete': '⚠️ No Remaining Access',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ fontSize: 15, fontWeight: 800, color: C.dark, pb: 0.5, pt: 2.5, px: 3 }}>
        {titleMap[step] ?? `Remove ${roleLabel}`}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 1.5, pb: 1 }}>
        {renderContent()}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, flexDirection: step === 'confirm-delete' ? 'column' : 'row', gap: step === 'confirm-delete' ? 0 : 1 }}>
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
}
